import type { ReactElement, ReactNode } from "react";
import { useCallback, useDeferredValue, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox, DropdownButton, IconButton } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import type { TaxonomyTreeRow } from "app/pages/sales-intelligence/utils/taxonomy-tree/types";

import { ScrollDismissBoundary } from "../../scroll-dismiss/ScrollDismissBoundary";
import { StyledBaseDropdownButtonWrap } from "../../styles";

import { StyledHeightComboboxContent, StyledTriggerSlot } from "./styles";
import { TreeRowOption } from "./TreeRowOption";
import type { TreeSelectionMode, TreeSelectSource } from "./types";

// Popover only — the default trigger fills the filter card like every other filter's trigger. The tree
// needs more room than the card offers, and the popover is portal-rendered so it may overhang the panel.
const POPOVER_WIDTH_PX = 350;

// What the filter panel's cards have always reserved. The tree there always overflows it, so holding it
// constant costs nothing and keeps the popover from resizing under the user mid-search.
const DEFAULT_CONTENT_MIN_HEIGHT_PX = 260;

export type { TreeSelectSource };

export type TreeSelectComboboxProps<TId extends string> = {
    viewModel: TreeSelectSource<TId>;
    onChange: (next: readonly TId[]) => void;
    /** Label for the default trigger. Unused when `trigger` is supplied. */
    buttonText?: string;
    searchPlaceholder: string;
    expandLabel: string;
    collapseLabel: string;
    dataAutomation: string;
    disabled?: boolean;
    /** See {@link TreeSelectionMode}. Defaults to `"multiple"`, the original behaviour. */
    selection?: TreeSelectionMode;
    /** Styles the default trigger as carrying a selection — the caller owns `buttonText`, so it cannot be derived. */
    hasValue?: boolean;
    /**
     * Replaces the default field trigger's contents; `buttonText` and `hasValue` then go unused, since the
     * node carries its own label and selected state. This is content, not the trigger element — the
     * `asChild` DOM node stays here (see `StyledTriggerSlot`), so a caller cannot leave the trigger inert.
     */
    trigger?: ReactNode;
    /**
     * Floor for the option list, in px. Defaults to the filter panel's reserved height; a surface whose
     * tree can be a handful of rows passes `0` so the popover fits its content instead of showing a void.
     */
    contentMinHeight?: number;
    /**
     * Nodes open on first render. Callers whose surface remounts on every commit use this to restore the
     * branch the user was working in, which expansion state alone cannot survive.
     */
    initialExpanded?: ReadonlySet<TId>;
};

export const TreeSelectCombobox = <TId extends string>({
    viewModel,
    onChange,
    buttonText,
    searchPlaceholder,
    expandLabel,
    collapseLabel,
    dataAutomation,
    disabled = false,
    selection = "multiple",
    hasValue = false,
    trigger,
    contentMinHeight = DEFAULT_CONTENT_MIN_HEIGHT_PX,
    initialExpanded,
}: TreeSelectComboboxProps<TId>): ReactElement => {
    const { translate, getLocale } = useTranslate();
    const locale = getLocale();

    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [expanded, setExpanded] = useState(() => new Set<TId>(initialExpanded));
    const deferredSearchTerm = useDeferredValue(searchTerm);

    if (disabled && isOpen) {
        setIsOpen(false);
        setSearchTerm("");
    }

    // Split from the rows memo below on purpose: expanding a node must not re-run search.
    const visible = useMemo(() => viewModel.visibleNodeIds(deferredSearchTerm), [viewModel, deferredSearchTerm]);

    const rows = useMemo(() => viewModel.rows(expanded, visible), [viewModel, expanded, visible]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (disabled && open) {
                return;
            }

            setIsOpen(open);

            if (!open) {
                setSearchTerm("");
            }
        },
        [disabled],
    );

    const handleToggleExpanded = useCallback((id: TId) => {
        setExpanded((previous) => {
            const next = new Set(previous);

            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }

            return next;
        });
    }, []);

    const handleToggleChecked = useCallback(
        (id: TId) => {
            onChange(viewModel.toggleChecked(id));

            // A single-select pick is the whole interaction; leaving the popover open would invite a
            // second one. Multi-select stays open so a run of picks costs one trip.
            if (selection === "single") {
                handleOpenChange(false);
            }
        },
        [onChange, viewModel, selection, handleOpenChange],
    );

    const renderRow = useCallback(
        (row: TaxonomyTreeRow<TId>) => (
            <TreeRowOption
                key={row.node.id}
                id={row.node.id}
                row={row}
                selection={selection}
                dataAutomation={dataAutomation}
                expandLabel={expandLabel}
                collapseLabel={collapseLabel}
                onToggleChecked={handleToggleChecked}
                onToggleExpanded={handleToggleExpanded}
                highlightRanges={viewModel.highlightRangesForSearch?.(deferredSearchTerm, row.node.id)}
                searchTerm={deferredSearchTerm}
                locale={locale}
            />
        ),
        [
            selection,
            dataAutomation,
            expandLabel,
            collapseLabel,
            handleToggleChecked,
            handleToggleExpanded,
            viewModel,
            deferredSearchTerm,
            locale,
        ],
    );

    const emptyState = useMemo(
        () => <Combobox.OptionLabel>{translate("dropdown.no.results")}</Combobox.OptionLabel>,
        [translate],
    );

    return (
        <Box
            width="100%"
            data-automation={dataAutomation}
        >
            <ScrollDismissBoundary
                isOpen={isOpen}
                onClose={() => handleOpenChange(false)}
            >
                <Combobox
                    isOpen={isOpen}
                    onOpenChange={handleOpenChange}
                    placement="bottom-start"
                    width={`${POPOVER_WIDTH_PX}px`}
                >
                    <Combobox.Trigger>
                        {/*
                         * Never move a caller's node up to be this direct child, and never hang an onClick
                         * here: floating-ui clones this element with the toggle handler, and `mergeProps`
                         * appends anything a caller adds *after* its own, too late to stop it. Kept as a
                         * descendant, a trigger's handlers still run first and can.
                         */}
                        <StyledTriggerSlot>
                            {trigger ?? (
                                <StyledBaseDropdownButtonWrap disabled={disabled}>
                                    <DropdownButton
                                        disabled={disabled}
                                        hasValue={hasValue}
                                    >
                                        {buttonText}
                                    </DropdownButton>
                                </StyledBaseDropdownButtonWrap>
                            )}
                        </StyledTriggerSlot>
                    </Combobox.Trigger>

                    <Combobox.SearchInput
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder={searchPlaceholder}
                    />

                    {searchTerm.length > 0 ? (
                        <Flex
                            position="absolute"
                            top="16px"
                            right="10px"
                            zIndex={1}
                            alignItems="center"
                        >
                            <IconButton
                                type="flat"
                                iconName="clear"
                                iconSize="xs"
                                aria-label={translate("common.clear")}
                                dataAutomation={`${dataAutomation}-search-clear`}
                                onClick={() => setSearchTerm("")}
                            />
                        </Flex>
                    ) : null}

                    {/*
                     * Virtualized mode ignores children and drives a react-aria ListBox whose selection
                     * model is binary. `selectionMode="none"` hands click handling back to the rows, so
                     * the checkbox can render the indeterminate state a tree needs.
                     */}
                    <StyledHeightComboboxContent
                        $minHeight={contentMinHeight}
                        virtualized
                        selectionMode="none"
                        items={rows as TaxonomyTreeRow<TId>[]}
                        renderItem={renderRow}
                        emptyState={emptyState}
                    />
                </Combobox>
            </ScrollDismissBoundary>
        </Box>
    );
};
